// ============================================
// OrdersInsightCard - AI Optimization & Insights
// ============================================

import { cn } from '../../../utils/cn';
import { useI18n } from '../../../i18n';

// ============================================
// Types
// ============================================

interface OrdersInsightCardProps {
  variant?: 'light' | 'dark';
  className?: string;
  onClick?: () => void;
}

// ============================================
// Icons
// ============================================

function SparklesIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  );
}

// ============================================
// Main Component
// ============================================

export function OrdersInsightCard({ variant = 'light', className, onClick }: OrdersInsightCardProps) {
  const isLight = variant === 'light';

  const { language } = useI18n();

  const text = {
    en: {
      title: 'AI Optimization',
      body: 'Our logistics model suggests consolidating orders to save +14% in shipping costs. Review order grouping opportunities based on destination and timing.',
      btn: 'Review Proposal',
      footnote: 'Updates every 15 minutes based on live traffic data.',
    },
    pl: {
      title: 'Optymalizacja AI',
      body: 'Nasz model logistyczny sugeruje konsolidację zamówień w celu zaoszczędzenia +14% kosztów wysyłki. Przejrzyj możliwości grupowania zamówień na podstawie miejsca docelowego i czasu.',
      btn: 'Przeglądaj Propozycję',
      footnote: 'Aktualizowane co 15 minut na podstawie danych o ruchu na żywo.',
    },
    uk: {
      title: 'AI-оптимізація',
      body: 'Наша логістична модель пропонує об’єднати замовлення, щоб заощадити +14% витрат на доставку. Перегляньте можливості групування замовлень за призначенням і часом.',
      btn: 'Переглянути пропозицію',
      footnote: 'Оновлюється кожні 15 хвилин на основі даних про трафік у реальному часі.',
    }
  }[language === 'pl' ? 'pl' : language === 'uk' ? 'uk' : 'en'];
  return (
    <div
      className={cn(
        'relative rounded-2xl p-6 overflow-hidden',
        isLight ? 'bg-[#F3F3FE]' : 'bg-gradient-to-br from-slate-900 to-slate-800',
        className
      )}
    >
      {/* Decorative blur circle */}
      <div 
        className={cn(
          'absolute -bottom-10 -right-10 w-40 h-40 rounded-full blur-3xl',
          isLight ? 'bg-primary-500/5' : 'bg-primary-500/20'
        )} 
      />

      {/* Icon */}
      <div className={cn(
        'w-10 h-10 rounded-full flex items-center justify-center mb-4',
        isLight ? 'bg-primary-600 text-white' : 'bg-white text-primary-600',
        'shadow-lg'
      )}>
        <SparklesIcon />
      </div>

      {/* Title */}
      <h3 className={cn(
        'font-manrope font-extrabold text-xl mb-2',
        isLight ? 'text-[#191B23]' : 'text-white'
      )}>
        {text.title}
      </h3>

      {/* Body */}
      <p className={cn(
        'text-sm leading-relaxed mb-4',
        isLight ? 'text-[#434655]' : 'text-white/80'
      )}>
        {text.body}
      </p>

      {/* CTA Button */}
      <button 
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all',
          isLight 
            ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white hover:shadow-lg hover:shadow-primary-500/25' 
            : 'bg-white text-primary-600 hover:bg-slate-100'
        )}
      >
        {text.btn}
        <ArrowRightIcon />
      </button>

      {/* Footnote */}
      <p className={cn(
        'text-xs mt-4',
        isLight ? 'text-[#737686]' : 'text-white/60'
      )}>
        {text.footnote}
      </p>
    </div>
  );
}

// ============================================
// Operations Insight Card - For pending orders view
// ============================================

interface OperationsInsightCardProps {
  title?: string;
  description?: string;
  onDismiss?: () => void;
  className?: string;
}

export function OperationsInsightCard({
  title = 'Heavy Inbound Load Detected',
  description = 'Our system predicts a 15% increase in unloading requirements this week. Consider scheduling additional warehouse staff for optimal throughput.',
  onDismiss,
  className,
}: OperationsInsightCardProps) {
  return (
    <div className={cn('relative bg-[#F3F3FE] rounded-xl p-6 overflow-hidden', className)}>
      {/* Decorative blur */}
      <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-primary-500/5 blur-3xl" />

      {/* Header */}
      <span className="text-sm font-bold text-primary-600 uppercase tracking-wider">
        Operations Insight
      </span>

      {/* Icon & Content */}
      <div className="flex gap-4 mt-4">
        <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center flex-shrink-0">
          <SparklesIcon />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-bold text-[#191B23] mb-1">{title}</h4>
          <p className="text-xs text-[#434655] leading-relaxed">{description}</p>
        </div>
      </div>

      {/* Dismiss */}
      {onDismiss && (
        <button 
          onClick={onDismiss}
          className="mt-4 text-[10px] font-bold text-primary-600 uppercase tracking-wide hover:underline"
        >
          Dismiss Recommendation
        </button>
      )}
    </div>
  );
}

// ============================================
// Supplier Performance Card
// ============================================

interface SupplierPerformanceMetric {
  label: string;
  value: number;
  color: string;
}

interface SupplierPerformanceCardProps {
  metrics?: SupplierPerformanceMetric[];
  className?: string;
}

export function SupplierPerformanceCard({
  metrics,
  className,
}: SupplierPerformanceCardProps) {
  const { t } = useI18n();

  const defaultMetrics = [
    { label: t('onTimeDeliveries') || 'On-Time Deliveries', value: 94.2, color: '#10B981' },
    { label: t('inProcessingRate') || 'In-Processing Rate', value: 81.0, color: '#004AC6' },
  ];

  const displayMetrics = metrics || defaultMetrics;

  return (
    <div className={cn('bg-white rounded-xl shadow-sm p-6', className)}>
      <h4 className="text-sm font-bold text-[#64748B] uppercase tracking-wider mb-4">
        {t('supplierPerformance') || 'Supplier Performance'}
      </h4>

      <div className="space-y-4">
        {displayMetrics.map((metric) => (
          <div key={metric.label}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: metric.color }}
                />
                <span className="text-xs font-bold text-[#191B23]">{metric.label}</span>
              </div>
              <span className="text-xs font-bold text-[#191B23]">{metric.value}%</span>
            </div>
            <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${metric.value}%`,
                  backgroundColor: metric.color,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
